import { useCallback, useContext, useEffect, useRef } from 'react';
import { ProductListContext } from '@jetshop/core/hooks/ProductList/ProductListContext';
import { useProductList } from '@jetshop/core/hooks/ProductList';

/**
 * Replaces the selected variant for a product in the favorites list.
 * Always results in exactly one list entry per product.
 */
export function usePersistFavoriteVariant(product) {
  const { usingLocalList } = useContext(ProductListContext);
  const { add, list, remove, update } = useProductList();
  const persistedVariantRef = useRef(product.variant?.articleNumber ?? null);

  useEffect(() => {
    persistedVariantRef.current = product.variant?.articleNumber ?? null;
  }, [product.variant?.articleNumber]);

  const persistVariant = useCallback(
    variantArticleNumber => {
      if (
        !variantArticleNumber ||
        persistedVariantRef.current === variantArticleNumber
      ) {
        return;
      }

      const articleNumber = product.articleNumber;
      const listItem = list?.items?.[articleNumber];
      const existingVariantKeys = Object.keys(listItem?.variants || {});
      const currentVariant =
        product.variant?.articleNumber || existingVariantKeys[0];
      const hasBaseOnly =
        listItem?.options && existingVariantKeys.length === 0;

      if (hasBaseOnly) {
        if (usingLocalList) {
          // update() merges variants in local state; remove+add replaces cleanly.
          // remove() always destructures 2nd argument
          remove(articleNumber, {});
          add(articleNumber, {
            variantArticleNumber,
            quantity: 1
          });
        } else {
          // Logged-in: update runs remove+add sequentially on the server.
          update(articleNumber, {
            variantArticleNumber,
            quantity: 1
          });
        }
      } else if (currentVariant) {
        if (listItem?.options) {
          remove(articleNumber, {});
        }

        update(articleNumber, {
          variantArticleNumber,
          variantToReplace: currentVariant,
          quantity: 1
        });
      } else if (!listItem) {
        add(articleNumber, {
          variantArticleNumber,
          quantity: 1
        });
      } else {
        update(articleNumber, {
          variantArticleNumber,
          quantity: 1
        });
      }

      persistedVariantRef.current = variantArticleNumber;
    },
    [
      add,
      list,
      product.articleNumber,
      product.variant,
      remove,
      update,
      usingLocalList
    ]
  );

  return persistVariant;
}
